import * as React from 'react';
import { cn } from '../../lib/utils';

export interface DashboardLayoutProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional hero block — typically a <PageHeader /> instance. */
  hero?: React.ReactNode;
  /**
   * Optional asides rendered alongside the main grid:
   *   - `before` lays out above the main content (e.g. global alerts)
   *   - `after`  lays out below (e.g. footer notes)
   */
  before?: React.ReactNode;
  after?: React.ReactNode;
}

/**
 * DashboardLayout — content shape for dashboard pages.
 *
 * Stacks a hero, optional pre-content block, the main content (typically a
 * responsive card grid composed by the consumer), and an optional after-block.
 * It does *not* impose a grid on `children` so consumers stay free to pick
 * any column structure they need (MetricCards in 3-up rows, DashboardCards
 * spanning 2 columns, etc.).
 */
export const DashboardLayout = React.forwardRef<
  HTMLDivElement,
  DashboardLayoutProps
>(({ hero, before, after, children, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('flex flex-col gap-6', className)}
      {...props}
    >
      {hero}
      {before}
      <div className="flex flex-col gap-5">{children}</div>
      {after}
    </div>
  );
});
DashboardLayout.displayName = 'DashboardLayout';

export interface DashboardGridProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of columns at the `lg` breakpoint. Defaults to 3. */
  columns?: 2 | 3 | 4;
}

const columnsMap: Record<NonNullable<DashboardGridProps['columns']>, string> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
};

/**
 * DashboardGrid — a convenience grid for stacking metric / dashboard cards.
 * Use it directly inside DashboardLayout, or compose your own grid.
 */
export const DashboardGrid = React.forwardRef<
  HTMLDivElement,
  DashboardGridProps
>(({ columns = 3, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'grid grid-cols-1 gap-4 md:grid-cols-2',
        columnsMap[columns],
        className,
      )}
      {...props}
    />
  );
});
DashboardGrid.displayName = 'DashboardGrid';
